import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Card, Layout, Space, Select } from 'antd';
import type { SimDataMeta } from '../types/domain';
import type { Period } from '../types/common';
import { isPrefectureCode } from '../utils/SimViewer/simdata';
import { useMetaData } from '../hooks/data/useMetaData';
import { Header } from '../layouts/Header';
import { Content } from '../layouts/Content';
import { JapanPolygons } from '../components/SimViewer/JapanPolygons';
import { Loading } from '../components/Loading';

const Home = (): ReactNode => {
  const { isLoading, customDataMetas, japanDataMetas, availablePeriods } =
    useMetaData();
  const { t } = useTranslation();

  const [hoveredPrefectureCode, setHoveredPrefectureCode] = useState<
    string | null
  >(null);
  const [selectedPeriod, setSelectedPeriod] = useState<Period | null>(null);

  if (isLoading) {
    return <Loading />;
  }
  return (
    <Layout>
      <Header />
      <Content isScrollable>
        {/* for All Japan GAD data */}
        {availablePeriods.size > 0 && (
          <FullDisplayContainer>
            <StyledSpace direction="vertical" size={0}>
              <PageTitle>{t('selectGadAllJapanData')}</PageTitle>
              <SelectContainer>
                <Select
                  value={selectedPeriod?.key}
                  options={Array.from(availablePeriods.values()).map(
                    (data): { value: string; label: string } => ({
                      value: data.key,
                      label: `${t('yearAndMonth', {
                        year: data.year,
                        month: data.month,
                      })} (${t(data.dayType)})`,
                    })
                  )}
                  onChange={(value: string): void => {
                    setSelectedPeriod(availablePeriods.get(value) || null);
                  }}
                  style={{ width: 240 }}
                />
                {hoveredPrefectureCode && (
                  <JapanSimDataTitle>
                    {isPrefectureCode(hoveredPrefectureCode)
                      ? t(`prefNames.${hoveredPrefectureCode}`)
                      : ''}
                  </JapanSimDataTitle>
                )}
              </SelectContainer>
            </StyledSpace>
            <JapanPolygons
              selectedPeriod={selectedPeriod}
              hoveredPrefectureCode={hoveredPrefectureCode}
              setHoveredPrefectureCode={setHoveredPrefectureCode}
            />
          </FullDisplayContainer>
        )}
        {/* for custom GAD data */}
        <StyledSpace direction="vertical" size={18}>
          <PageTitle>
            {japanDataMetas.size === 0
              ? t('selectData')
              : customDataMetas.size > 0
                ? t('selectCustomizedData')
                : ''}
          </PageTitle>
          <Space align="start" wrap size="middle">
            {Array.from(customDataMetas.values()).map(
              (dataMeta: SimDataMeta): ReactNode => {
                return (
                  <Link
                    key={dataMeta.simdata_id}
                    to={`/sim-viewer/${dataMeta.simdata_id}`}
                  >
                    <SimDataItem hoverable={true}>
                      <Card.Meta
                        title={<SimDataTitle>{dataMeta.name}</SimDataTitle>}
                        description={
                          <SimDataDescription>
                            {dataMeta.description}
                          </SimDataDescription>
                        }
                      />
                    </SimDataItem>
                  </Link>
                );
              }
            )}
          </Space>
        </StyledSpace>
      </Content>
    </Layout>
  );
};

export { Home };

const FullDisplayContainer = styled.div`
  width: 100%;
  height: calc(100vh - 100px);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
`;

const StyledSpace = styled(Space)`
  padding: 35px 31px;
`;

const PageTitle = styled.div`
  font: normal normal normal 34px/46px Segoe UI;
  margin-bottom: 16px;
`;

const SimDataItem = styled(Card)`
  width: 380px;
`;

const SimDataTitle = styled.div`
  white-space: normal;
`;

const SimDataDescription = styled.div`
  white-space: pre-wrap;
`;

const JapanSimDataTitle = styled.div`
  white-space: normal;
  font: normal normal normal 16px/16px Segoe UI;
`;

const SelectContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
  white-space: normal;
`;
